import React, { useState } from 'react';
import { 
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  IconButton,
  Box,
  Tooltip
} from '@mui/material';
import { Deal } from './DealPieChart';
import { formatDate } from './RecentDealsList';
import DeleteIcon from '@mui/icons-material/Delete';
import NoteIcon from '@mui/icons-material/Note';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';

// Helper function to calculate days remaining until closing date
const getDaysRemaining = (closingDate: string | null): number | null => {
  if (!closingDate) return null;
  
  try {
    const closing = new Date(closingDate);
    const today = new Date();
    
    // Reset the time portion to ensure we're comparing full days
    closing.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    // Calculate the difference in milliseconds and convert to days
    const diffTime = closing.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  } catch (error) {
    return null;
  }
};

// Function to get color based on days remaining
const getClosingDateColor = (daysRemaining: number | null): string => {
  if (daysRemaining === null) return 'text.secondary'; // Default color for no date
  
  if (daysRemaining < 0) return 'text.disabled'; // Grey for past dates
  if (daysRemaining <= 7) return 'error.main'; // Red for 7 or fewer days
  if (daysRemaining <= 14) return 'warning.main'; // Orange for 8-14 days
  return 'success.main'; // Green for more than 14 days
};

interface AllDealsDialogProps {
  open: boolean;
  onClose: () => void;
  deals: Deal[];
  onDeleteClick: (deal: Deal) => void;
  onNotesClick?: (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => void;
  onCofClick?: (event: React.MouseEvent<HTMLButtonElement>, deal: Deal) => void;
}

const AllDealsDialog: React.FC<AllDealsDialogProps> = ({ 
  open, 
  onClose, 
  deals, 
  onDeleteClick,
  onNotesClick,
  onCofClick
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sort deals by most recent dates
  const sortedDeals = [...deals].sort((a, b) => {
    const dateA = a.cof || a.closing_date || a.submission_date;
    const dateB = b.cof || b.closing_date || b.submission_date;
    
    if (!dateA) return 1;
    if (!dateB) return -1;
    
    return new Date(dateB).getTime() - new Date(dateA).getTime();
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle>All Deals</DialogTitle>
      <DialogContent>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Deal Number</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>COF Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Closing Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Mortgage Amount</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>BFS Stage</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Notes</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedDeals
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((deal, index) => {
                  const daysRemaining = getDaysRemaining(deal.closing_date);
                  const closingColor = getClosingDateColor(daysRemaining);
                  
                  // Get closing status text
                  const getClosingStatusText = () => {
                    if (daysRemaining === null) return "";
                    if (daysRemaining < 0) return ` (${Math.abs(daysRemaining)} days past)`;
                    return ` (${daysRemaining} days left)`;
                  };
                  
                  return (
                    <TableRow key={`all-deals-${deal.id}-${index}`}>
                      <TableCell>#{deal.deal_number}</TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              mr: 1
                            }}
                          >
                            {formatDate(deal.cof)}
                          </Typography>
                          {onCofClick && (
                            <Tooltip title="Edit COF Date">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(e) => onCofClick(e, deal)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <CalendarTodayIcon 
                            fontSize="small" 
                            sx={{ mr: 0.5, color: closingColor }} 
                          />
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: closingColor, 
                              fontWeight: daysRemaining !== null && daysRemaining <= 14 ? 'bold' : 'normal'
                            }}
                          >
                            {formatDate(deal.closing_date)}
                            {getClosingStatusText()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{deal.mortgage_amount}</TableCell>
                      <TableCell>{deal.mtg_type}</TableCell>
                      <TableCell>{deal.bfs_stage}</TableCell>
                      <TableCell>
                        {onNotesClick && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(e) => onNotesClick(e, deal)}
                            title="View/Edit Notes"
                          >
                            <NoteIcon fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onDeleteClick(deal)}
                          title="Delete Deal"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {deals.length === 0 && (
                <TableRow key="no-deals-found-dialog">
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" color="text.secondary" sx={{ py: 3 }}>
                      No deals found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={deals.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AllDealsDialog; 